import RemoveIcon from '@material-symbols/svg-300/rounded/remove.svg'
import classnames from 'classnames'
import { useFormik } from 'formik'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Group } from '../../../models/Group'
import useRemoveMembersFromGroups from '../../../state/mutations/useRemoveMembersFromGroups'
import { useUsers } from '../../../state/entities'
import useItemSelection from '../../../utilities/useItemSelection'
import { ItemSelectorInit } from '../../../utilities/useItemSelector'
import Button from '../../basics/Button/Button'
import FormButtons from '../../basics/FormButtons/FormButtons'
import UserSlugs from '../../slugs/UserSlugs'

import styles from './RemoveGroupMembershipsFromUsersForm.module.scss'

const groupSelectionInit: ItemSelectorInit<Group, Group> = {
  createItem: (base) => ({ ...base }),
}

interface RemoveGroupMembershipsFromUsersFormProps {
  userUuids: string[]
  onClose: () => void
}

export default React.memo(function RemoveGroupMembershipsFromUsersForm({
  userUuids,
  onClose,
}: RemoveGroupMembershipsFromUsersFormProps) {
  const { t } = useTranslation()

  // Users
  const { users } = useUsers(userUuids)

  // Groups
  const groups = useMemo(() => {
    const groups = []
    const seen = new Set()
    for (const user of users) {
      for (const group of user.groups || []) {
        if (!seen.has(group.uuid)) {
          seen.add(group.uuid)
          groups.push(group)
        }
      }
    }
    return groups
  }, [users])

  // Selected groups
  const groupSelection = useItemSelection(groupSelectionInit, groups)

  // Form
  const removeMembersFromGroups = useRemoveMembersFromGroups()
  const formik = useFormik({
    initialValues: {
      userUuids: userUuids,
      groupUuids: [],
    },
    onSubmit: async (values, { setStatus }) => {
      setStatus()
      try {
        await removeMembersFromGroups.mutateAsync(values)
        onClose()
      } catch (err) {
        setStatus(err)
      }
    },
  })

  useEffect(() => {
    formik.setFieldValue(
      'groupUuids',
      groupSelection.items.map(({ uuid }) => uuid),
      true
    )
  }, [groupSelection.items])

  return (
    <form noValidate onSubmit={formik.handleSubmit} className={classnames(styles.RemoveGroupMembershipsFromUsersForm)}>
      <header>
        <hgroup>
          <h1>{t('manage group.remove from groups')}</h1>
        </hgroup>
      </header>

      <UserSlugs uuids={formik.values.userUuids} max={5} className={styles.users} />

      <div className={styles.groups}>
        <div className={styles.availableGroups}>
          <h2>{t('manage group.available groups')}</h2>
          <ul>
            {groups
              .filter(({ uuid }) => !groupSelection.ids[uuid])
              .map((group) => (
                <li key={group.uuid}>
                  <Button data-item-id={group.uuid} onClick={groupSelection.handleSelected} small>
                    <RemoveIcon />
                  </Button>
                  {group.name}
                </li>
              ))}
          </ul>
        </div>

        <div className={styles.selectedGroups}>
          <h2>{t('manage group.selected groups')}</h2>
          <ul>
            {groupSelection.items.map((group) => (
              <li key={group.uuid}>
                <Button data-item-id={group.uuid} onClick={groupSelection.handleDeselected} small>
                  <RemoveIcon />
                </Button>
                {group.name}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <FormButtons onClose={onClose} {...formik} />
    </form>
  )
})
